import { ModuleWrapper } from '@/components'
import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import uniq from 'lodash/uniq'
import { useEffect, useMemo } from 'react'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import { Button, Card, Input } from '../../../../components_deprecated'
import { useAdmin, useBreadcrumbs, useCompany } from '../../../../utils'
import { usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { PageType } from '../../../common/consts'
import { ExportFormat, ExportOption } from '../../../exports'
import { ALL_VEHICLES_QUERY } from '../../queries'
import type {
  CreateVehicleByRegNrModalResolve,
  VehicleFilter,
} from '../../types'
import type {
  AllVehiclesQuery,
  AllVehiclesQueryVariables,
} from '../../types.graphql'
import {
  useEquipmentExportMutations,
  useVehicleMutations,
} from '../mutations.hooks'
import { CreateVehicleByRegNrModal } from './CreateVehicleByRegNrModal'
import { ExportEquipmentModal } from './ExportEquipmentModal'
import { FleetFilter } from './FleetFilter'
import { FleetTable } from './FleetTable'

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: column;
  gap: 1rem;

  margin-bottom: 1rem;
  padding: 1rem;

  ${isMobileOnly &&
  `
    grid-auto-flow: row;
  `}
`

const ExportButton = styled(Button)`
  margin-left: auto;
  padding: 0 2rem;
  font-weight: 500;

  ${isMobileOnly &&
  `
    margin-left: 0;
    order: -1;
    width: 100%;
  `}
`

type StyledBadgeProps = {
  active: boolean
}

const StyledBadge = styled.div<StyledBadgeProps>`
  display: flex;
  height: fit-content;
  background: ${props =>
    props.active ? props.theme.colors.secondary : props.theme.colors.gray8};
  padding: 0.3rem 0.8rem;
  width: fit-content;
  font-size: 12px;
  border-radius: ${props => props.theme.standardBorderRadius};
  color: ${props =>
    props.active ? props.theme.colors.white : props.theme.colors.black};
`

const ContainerBadge = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  row-gap: 0.5rem;

  & > h4 {
    margin: 0 1rem 0 0;
  }
`

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const DEFAULT = true

function EquipmentTab() {
  const translations = useTranslate({
    equipment: 'common.equipment',
    addVehicle: 'vehicles.add-vehicle',
    export: 'common.export',
    owned: 'vehicles.owned',
    type: 'common.type',
    unowned: 'vehicles.unowned',
  })
  const isAdmin = useAdmin()
  const { hasPermissions } = usePermissions()
  const canAdd = hasPermissions(PERMISSIONS.vehicles.add.vehicle, true)
  const addPrompt = usePrompt()
  const { mutations } = useVehicleMutations()
  const { setOverrides, overrides } = useBreadcrumbs()
  const company = useCompany()

  /* CreateModal */
  async function handleCreateByRegNrVehicle() {
    const { data } = await addPrompt<CreateVehicleByRegNrModalResolve | null>(
      resolve => <CreateVehicleByRegNrModal onSubmit={resolve} />
    )
    if (!data) return
    const { form } = data
    await mutations.createByRegNr({
      variables: {
        input: form,
      },
    })
  }

  /* Forms */

  const {
    formValues: filter,
    formChangeHandler: handler,
    updateForm,
  } = useForm<VehicleFilter>({
    values: {
      query: undefined,
      owned: true,
      orderBy: 'registrationNumber',
      includedTypes: [],
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        sessionStorage: true,
        storeFormStateName: 'fleet-overview-filters',
        transformValues: {},
      },
    },
  })
  const { data, loading } = useQuery<
    AllVehiclesQuery,
    AllVehiclesQueryVariables
  >(ALL_VEHICLES_QUERY, {
    variables: {
      q: filter.query,
      currentlyOwned: filter.owned,
      orderBy: filter.orderBy,
    },
  })

  const vehicles = useMemo(
    () => data?.allVehicles.edges.map(edge => edge.node) ?? [],
    [data]
  )
  const vehicleTypes = useMemo(
    () =>
      uniq(vehicles.map(vehicle => vehicle.vehicleType)).filter(
        type => type !== null
      ),
    [vehicles]
  )

  const includedVehicles =
    filter.includedTypes.length === 0
      ? vehicles
      : vehicles.filter(v => filter.includedTypes.includes(v.vehicleType))

  useEffect(() => {
    if (overrides['vehicle-fleet']) return
    setOverrides({ 'vehicle-fleet': translations.equipment })
  }, [overrides, setOverrides, translations.equipment])

  const { exportEquipments } = useEquipmentExportMutations()

  async function doExport(values: { exportFormat: ExportFormat }) {
    await exportEquipments({
      variables: {
        exportType: 'vehicles',
        company: company.id,
        exportFormat: values.exportFormat,
        exportOption:
          values.exportFormat === ExportFormat.PDF
            ? ExportOption.DOWNLOAD
            : ExportOption.SAVE,
      },
    })
  }

  async function handleExport() {
    const { data } = await addPrompt<{ exportFormat: ExportFormat } | null>(
      resolve => <ExportEquipmentModal onSubmit={resolve} />
    )
    if (!data) return

    await doExport(data)
  }

  return (
    <ModuleWrapper
      className="p-0 md:px-0 md:pt-0 md:pb-0"
      title={translations.equipment}
      page={PageType.VEHICLES}
      headerRight={
        isAdmin && (
          <ExportButton
            iconLeftProps={{ icon: 'download' }}
            fullWidth={isMobileOnly}
            disabled={loading}
            onClick={handleExport}
          >
            <span>{translations.export}</span>
          </ExportButton>
        )
      }
    >
      <Controls>
        <Input
          value={filter.query ?? ''}
          type="search"
          fullWidth
          onChange={handler('query')}
        />

        <FleetFilter
          filter={filter}
          vehicleTypes={vehicleTypes}
          onUpdate={updateForm}
        />

        {canAdd && (
          <Button
            iconLeftProps={{ icon: 'plus', size: '1rem', type: 'solid' }}
            color="primary"
            onClick={handleCreateByRegNrVehicle}
          >
            {translations.addVehicle}
          </Button>
        )}
      </Controls>

      {(filter.owned !== DEFAULT ||
        (vehicleTypes?.length > 0 && filter.includedTypes.length > 0)) && (
        <Controls>
          <RowContainer>
            <ContainerBadge>
              <h4>{translations.owned}</h4>

              <StyledBadge active={true}>
                {filter.owned ? translations.owned : translations.unowned}
              </StyledBadge>
            </ContainerBadge>

            {vehicleTypes?.length > 0 && filter.includedTypes.length > 0 && (
              <ContainerBadge>
                <h4>{translations.type}</h4>
                {vehicleTypes.map(vehicle => {
                  const checked =
                    filter.includedTypes.length > 0 &&
                    filter.includedTypes.includes(vehicle)
                  if (!checked) return null
                  return (
                    <StyledBadge key={vehicle} active={true}>
                      {vehicle}
                    </StyledBadge>
                  )
                })}
              </ContainerBadge>
            )}
          </RowContainer>
        </Controls>
      )}

      <FleetTable
        vehicles={includedVehicles}
        filter={filter}
        onSort={handler('orderBy')}
      />
    </ModuleWrapper>
  )
}

export default EquipmentTab
